import { cloneElement, Fragment, isValidElement, type ReactElement, type ReactNode } from "react";

import type { TranslationKey, TranslationOptions, Translations, TranslationVariables } from "./types";
import { useTranslation } from "./useTranslation";

type TransComponents = { [key: string]: ReactElement | ((value: string) => ReactElement) };

type TransProps<T extends Record<string, string> = Translations, TKey extends TranslationKey<T> = TranslationKey> = {
  i18nKey: TKey;
  values?: TranslationVariables<TKey, T>;
  components?: TransComponents;
};

export function Trans<
  T extends Record<string, string> = Translations,
  TKey extends TranslationKey<T> = TranslationKey<T>,
>({ i18nKey, values, components }: TransProps<T, TKey>): ReactNode {
  const {
    t,
    i18n: { additionalOptions },
  } = useTranslation();
  const translation = t(i18nKey as any, values);

  const shouldParse = !!components || !!additionalOptions?.transKeepBasicHtmlNodesFor?.length;
  if (!shouldParse) {
    return translation;
  }

  return parseTranslation({
    text: translation,
    components,
    keyPrefix: "",
    additionalOptions,
  });
}

function parseTranslation({
  text,
  components,
  keyPrefix,
  additionalOptions,
}: {
  text: string;
  components?: TransComponents;
  keyPrefix?: string;
  additionalOptions?: TranslationOptions;
}): ReactNode[] {
  const nodes: ReactNode[] = [];
  let remaining = text;
  let counter = 0;
  const prefix = keyPrefix ?? "";

  // Regex to match either a self-closing tag (like <br />) or a paired tag (like <a>...</a>)
  const regex = /<(\w+)(?:\s*\/>|>(.*?)<\/\1>)/;

  while (true) {
    const match = regex.exec(remaining);
    if (!match) {
      if (remaining) {
        counter += 1;
        nodes.push(<Fragment key={`${keyPrefix}${counter}`}>{remaining}</Fragment>);
      }
      break;
    }
    const [fullMatch, tag, innerText] = match;
    const index = match.index;
    if (index > 0) {
      nodes.push(<Fragment key={`${prefix}${counter++}`}>{remaining.slice(0, index)}</Fragment>);
    }
    const key = `${prefix}${counter++}`;
    nodes.push(renderTagNode({ tag, innerText, fullMatch, key, counter, prefix, components, additionalOptions }));
    remaining = remaining.slice(index + fullMatch.length);
  }

  return nodes;
}

function renderTagNode({
  tag,
  innerText,
  fullMatch,
  key,
  counter,
  prefix,
  components,
  additionalOptions,
}: {
  tag: string;
  innerText: string | undefined;
  fullMatch: string;
  key: string;
  counter: number;
  prefix: string;
  components?: TransComponents;
  additionalOptions?: TranslationOptions;
}): ReactNode {
  const component = components?.[tag];
  if (component) {
    return renderComponentNode({ component, innerText, key, counter, prefix, components, additionalOptions });
  }
  if (additionalOptions?.transKeepBasicHtmlNodesFor.includes(tag)) {
    const Tag = tag as React.ElementType;

    return innerText ? <Tag key={key}>{innerText}</Tag> : <Tag key={key} />;
  }

  return <Fragment key={key}>{fullMatch}</Fragment>;
}

function renderComponentNode({
  component,
  innerText,
  key,
  counter,
  prefix,
  components,
  additionalOptions,
}: {
  component: ReactElement | ((value: string) => ReactElement);
  innerText: string | undefined;
  key: string;
  counter: number;
  prefix: string;
  components?: TransComponents;
  additionalOptions?: TranslationOptions;
}): ReactNode {
  if (innerText === undefined) {
    return cloneElement(isValidElement(component) ? component : component(""), { key });
  }
  const children = parseTranslation({
    text: innerText,
    components,
    keyPrefix: `${prefix}${counter}_`,
    additionalOptions,
  });

  return isValidElement(component)
    ? cloneElement(component, { key, children } as any as ReactElement)
    : cloneElement(component(innerText), { key });
}
